import { Translation } from '../../../models/translation';
import { TranslateService } from '@ngx-translate/core';
import { ProductionViewMode } from '../../../services/base-actions/production-inventory.service';

export interface FilterField {
  name: ProductionFilters;
  label: string;
  type: 'text' | 'select' | 'autocomplete' | 'multiselect';
}

// Short labels for filter inputs (tooltips show full names)
// Note: Production Card and Client PO are not translated (kept in English)
export const filtersTranslations: Record<string, Translation> = {
  // Roll filters
  rollNumbers: { es: 'Rollo #', en: 'Roll #' },
  itemCodes: { es: 'Artículo', en: 'Item' },
  fabricCodes: { es: 'Tela', en: 'Fabric' },
  locationCodes: { es: 'Ubicación', en: 'Location' },
  knittingOrderReferences: { es: 'Orden Tejido', en: 'Knitting Order' },
  productionCardReferences: { es: 'Prod. Card', en: 'Prod. Card' },
  dispatchInstructionReferences: { es: 'Instruc. Desp.', en: 'Dispatch Instr.' },
  workOrderReferences: { es: 'Orden Trabajo', en: 'Work Order' },
  clientProductOrderNumbers: { es: 'Client PO', en: 'Client PO' },
  rollStatuses: { es: 'Estado', en: 'Status' },
  categories: { es: 'Categoría', en: 'Category' },
  qualities: { es: 'Calidad', en: 'Quality' },

  // Production Card filters
  productionCardNumbers: { es: 'Production Card #', en: 'Production Card #' },
  statuses: { es: 'Estado', en: 'Status' },
  clientPOs: { es: 'Client PO', en: 'Client PO' },
  colorReferences: { es: 'Lab Dip', en: 'Lab Dip' },
  assetIds: { es: 'Máquina', en: 'Machine' },
  dyeingForecastReferences: { es: 'Pronóstico', en: 'Forecast' },
};

// Full names for tooltips (shown on hover)
// Note: Production Card and Client PO are not translated (kept in English)
export const filtersTooltipTranslations: Record<string, Translation> = {
  // Roll filters
  rollNumbers: { es: 'Número de Rollo', en: 'Roll Number' },
  itemCodes: { es: 'Código de Artículo', en: 'Item Code' },
  fabricCodes: { es: 'Código de Tela', en: 'Fabric Code' },
  locationCodes: { es: 'Código de Ubicación', en: 'Location Code' },
  knittingOrderReferences: { es: 'Referencia de Orden de Tejido', en: 'Knitting Order Reference' },
  productionCardReferences: { es: 'Production Card Reference', en: 'Production Card Reference' },
  dispatchInstructionReferences: { es: 'Referencia de Instrucción de Despacho', en: 'Dispatch Instruction Reference' },
  workOrderReferences: { es: 'Referencia de Orden de Trabajo', en: 'Work Order Reference' },
  clientProductOrderNumbers: { es: 'Client Product Order Number', en: 'Client Product Order Number' },
  rollStatuses: { es: 'Estado del Rollo', en: 'Roll Status' },
  categories: { es: 'Categoría del Rollo', en: 'Roll Category' },
  qualities: { es: 'Calidad del Rollo', en: 'Roll Quality' },

  // Production Card filters
  productionCardNumbers: { es: 'Production Card Number', en: 'Production Card Number' },
  statuses: { es: 'Estado de la Tarjeta', en: 'Card Status' },
  clientPOs: { es: 'Client Purchase Order', en: 'Client Purchase Order' },
  colorReferences: { es: 'Lab Dip (Número - Color)', en: 'Lab Dip (Number - Color)' },
  assetIds: { es: 'ID de Máquina de Teñido', en: 'Dyeing Machine ID' },
  dyeingForecastReferences: { es: 'Referencia de Pronóstico de Teñido', en: 'Dyeing Forecast Reference' },
};

export type ProductionFilters =
  // Roll filters
  | 'rollNumbers'
  | 'itemCodes'
  | 'fabricCodes'
  | 'locationCodes'
  | 'knittingOrderReferences'
  | 'productionCardReferences'
  | 'dispatchInstructionReferences'
  | 'workOrderReferences'
  | 'clientProductOrderNumbers'
  | 'rollStatuses'
  | 'categories'
  | 'qualities'
  // Production Card filters
  | 'productionCardNumbers'
  | 'statuses'
  | 'clientPOs'
  | 'colorReferences'
  | 'assetIds'
  | 'dyeingForecastReferences';

/**
 * Get available filters for Roll view
 */
export function getRollFilters(translateService: TranslateService): FilterField[] {
  // Note: locationCodes is handled by the TreeSelect (commented out for now)
  // Note: clientProductOrderNumbers is commented out for now
  return [
    { name: 'rollNumbers', label: translateService.instant('Production_Roll_Numbers') || 'Roll #', type: 'text' },
    { name: 'itemCodes', label: translateService.instant('Production_Item_Codes') || 'Item', type: 'autocomplete' },
    { name: 'fabricCodes', label: translateService.instant('Production_Fabric_Codes') || 'Fabric', type: 'autocomplete' },
    { name: 'rollStatuses', label: translateService.instant('Production_Roll_Statuses') || 'Status', type: 'multiselect' },
    { name: 'categories', label: translateService.instant('Production_Categories') || 'Category', type: 'multiselect' },
    { name: 'qualities', label: translateService.instant('Production_Qualities') || 'Quality', type: 'multiselect' },
    { name: 'workOrderReferences', label: translateService.instant('Production_Work_Orders') || 'Work Order', type: 'autocomplete' },
    { name: 'productionCardReferences', label: translateService.instant('Production_Card_Refs') || 'Prod. Card', type: 'autocomplete' },
    {
      name: 'knittingOrderReferences',
      label: translateService.instant('Production_Knitting_Orders') || 'Knitting Order',
      type: 'autocomplete',
    },
    {
      name: 'dispatchInstructionReferences',
      label: translateService.instant('Production_Dispatch_Instructions') || 'Dispatch Instr.',
      type: 'autocomplete',
    },
    // { name: 'clientProductOrderNumbers', label: translateService.instant('Production_Client_PO') || 'Client PO', type: 'text' },
  ];
}

/**
 * Get available filters for Production Card view
 */
export function getProductionCardFilters(translateService: TranslateService): FilterField[] {
  // Note: clientPOs is commented out for now
  return [
    { name: 'productionCardNumbers', label: translateService.instant('Production_Card_Numbers') || 'Production Card #', type: 'text' },
    { name: 'workOrderReferences', label: translateService.instant('Production_Work_Orders') || 'Work Order', type: 'autocomplete' },
    { name: 'statuses', label: translateService.instant('Production_Statuses') || 'Status', type: 'multiselect' },
    // { name: 'clientPOs', label: translateService.instant('Production_Client_POs') || 'Client PO', type: 'text' },
    { name: 'colorReferences', label: translateService.instant('Production_Lab_Dip') || 'Lab Dip', type: 'autocomplete' },
  ];
}

/**
 * Get filters for the specified view mode
 */
export function getFiltersForViewMode(viewMode: ProductionViewMode, translateService: TranslateService): FilterField[] {
  if (viewMode === 'roll') {
    return getRollFilters(translateService);
  }
  return getProductionCardFilters(translateService);
}
